/**
 * Searchable filter comboboxes — select + type-to-search + visible option list.
 */
class TnwsFilterCombobox {
  constructor(root) {
    this.root = root;
    this.select = root.querySelector('select');
    this.input = root.querySelector('[data-tnws-filter-combobox-input]');
    this.list = root.querySelector('[data-tnws-filter-combobox-list]');
    this.allOptions = [];
    this.emptyLabel = '';
    this.bound = false;
    this.useColorSwatches = Boolean(this.select?.hasAttribute('data-tnws-filter-color-select'));
    this.inputSwatch = null;
    if (!this.select || !this.input) return;
    if (this.useColorSwatches && window.TnwsColorSwatches) {
      this.root.classList.add('tnws-fav-filters__combobox--color-swatches');
      this.setupInputSwatch();
    }
    this.snapshotOptions();
    this.bind();
    if (this.select.value) {
      const label = this.select.options[this.select.selectedIndex]?.textContent?.trim() || '';
      this.updateInputSwatch(label);
    }
  }

  setupInputSwatch() {
    const wrap = document.createElement('div');
    wrap.className = 'tnws-fav-filters__combobox-input-wrap';
    this.inputSwatch = window.TnwsColorSwatches.createSwatchElement('');
    this.inputSwatch.classList.add('tnws-fav-filters__color-swatch--input');
    this.inputSwatch.hidden = true;
    this.input.parentNode.insertBefore(wrap, this.input);
    wrap.appendChild(this.inputSwatch);
    wrap.appendChild(this.input);
  }

  updateInputSwatch(label = '') {
    if (!this.inputSwatch || !window.TnwsColorSwatches) return;
    const text = String(label || '').trim();
    if (!text || text === this.emptyLabel) {
      this.inputSwatch.hidden = true;
      return;
    }
    window.TnwsColorSwatches.applyToElement(this.inputSwatch, text);
  }

  snapshotOptions() {
    if (!this.select) return;
    const options = [...this.select.options];
    this.emptyLabel = options[0]?.textContent?.trim() || '';
    this.allOptions = options.slice(1).map((option) => ({
      value: option.value,
      label: option.textContent.trim(),
    }));
  }

  getFilteredOptions(filterText = '') {
    const query = String(filterText || '').trim().toLowerCase();
    return query
      ? this.allOptions.filter((option) => option.label.toLowerCase().includes(query))
      : this.allOptions;
  }

  rebuildOptions(filterText = '') {
    if (!this.select) return;
    const query = String(filterText || '').trim().toLowerCase();
    const current = this.select.value;
    const filtered = this.getFilteredOptions(filterText);

    this.select.replaceChildren();
    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = this.emptyLabel;
    this.select.appendChild(emptyOption);
    filtered.forEach((option) => {
      const el = document.createElement('option');
      el.value = option.value;
      el.textContent = option.label;
      this.select.appendChild(el);
    });

    if (current && filtered.some((option) => option.value === current)) {
      this.select.value = current;
    } else if (query && filtered.length === 1) {
      this.select.value = filtered[0].value;
    } else {
      this.select.value = '';
    }

    this.renderList(filtered, query);
  }

  renderList(options, query = '') {
    if (!this.list) return;

    this.list.replaceChildren();
    if (!options.length) {
      const empty = document.createElement('li');
      empty.className = 'tnws-fav-filters__combobox-list-empty';
      empty.textContent = query ? 'No matches' : 'No options';
      this.list.appendChild(empty);
      this.list.hidden = false;
      return;
    }

    options.forEach((option) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tnws-fav-filters__combobox-list-option';
      button.setAttribute('role', 'option');
      button.dataset.value = option.value;
      if (this.useColorSwatches && window.TnwsColorSwatches) {
        button.appendChild(window.TnwsColorSwatches.createSwatchElement(option.label));
        const labelEl = document.createElement('span');
        labelEl.className = 'tnws-fav-filters__combobox-list-option-label';
        labelEl.textContent = option.label;
        button.appendChild(labelEl);
      } else {
        button.textContent = option.label;
      }
      if (this.select.value === option.value) button.classList.add('is-selected');
      button.addEventListener('click', () => this.chooseOption(option));
      item.appendChild(button);
      this.list.appendChild(item);
    });

    this.list.hidden = false;
  }

  chooseOption(option) {
    if (!this.select) return;
    this.select.value = option.value;
    this.input.value = option.label;
    this.updateInputSwatch(option.label);
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.rebuildOptions(this.input.value);
  }

  showList() {
    if (!this.list) return;
    this.rebuildOptions(this.input.value);
  }

  bind() {
    if (this.bound) return;
    this.bound = true;

    this.input.addEventListener('input', () => {
      const previousValue = this.select.value;
      this.rebuildOptions(this.input.value);
      if (this.select.value !== previousValue) {
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    this.input.addEventListener('search', () => {
      const previousValue = this.select.value;
      this.rebuildOptions(this.input.value);
      if (this.select.value !== previousValue) {
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    this.input.addEventListener('focus', () => this.showList());

    this.select.addEventListener('change', () => {
      if (this.select.value) {
        const label = this.select.options[this.select.selectedIndex]?.textContent?.trim() || '';
        if (label) this.input.value = label;
        this.updateInputSwatch(label);
      } else {
        this.input.value = '';
        this.updateInputSwatch('');
      }
      this.rebuildOptions(this.input.value);
    });

    this.select.addEventListener('focus', () => this.showList());
  }

  refresh() {
    const text = this.input?.value || '';
    this.snapshotOptions();
    this.rebuildOptions(text);
    if (this.select?.value) {
      const label = this.select.options[this.select.selectedIndex]?.textContent?.trim() || text;
      this.updateInputSwatch(label);
    } else {
      this.updateInputSwatch('');
    }
    if (this.allOptions.length && this.isOpenInUi()) {
      this.showList();
    }
  }

  isOpenInUi() {
    if (!this.root?.isConnected) return false;
    const accordion = this.root.closest('.tnws-fav-filters__accordion');
    if (accordion && !accordion.open) return false;
    let node = this.root;
    while (node) {
      if (node.hidden) return false;
      node = node.parentElement;
    }
    return true;
  }

  static initAll(root = document) {
    const map = TnwsFilterCombobox.instances;
    root.querySelectorAll('[data-tnws-filter-combobox]').forEach((element) => {
      if (map.has(element)) {
        map.get(element).refresh();
        return;
      }
      const instance = new TnwsFilterCombobox(element);
      if (instance.select && instance.input) map.set(element, instance);
    });
  }

  static refreshAll(root = document) {
    root.querySelectorAll('[data-tnws-filter-combobox]').forEach((element) => {
      const instance = TnwsFilterCombobox.instances.get(element);
      if (instance) instance.refresh();
      else TnwsFilterCombobox.initAll(element.parentElement || root);
    });
  }
}

TnwsFilterCombobox.instances = new WeakMap();
window.TnwsFilterCombobox = TnwsFilterCombobox;
